import {
    ActionReducerMapBuilder,
    createEntityAdapter,
    createSlice,
    EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../../../app/store";

import {
    fetchLegacyReasonedOrdersThunk,
    fetchSinglePartyLegacyROReportThunk,
    createLegacyReasonedOrderThunk,
    upsertLegacyReasonedOrderThunk,

    fetchReasonedOrdersThunk,
    createReasonedOrderThunk,
    fetchSinglePartyROReportThunk,
    upsertReasonedOrderThunk,
} from "./thunks";

import { ROReport } from "../../../../../api/reasonedOrder";
import { ReportStatus } from "../../../../../api/Reports";

export interface ROReportsState {
    reasoned_orders: EntityState<ROReport, string>;
    status: "idle" | "loading" | "succeeded" | "saving" | "failed";
    error: string | null;
    report_status: ReportStatus | null;
}

const roAdapter = createEntityAdapter<ROReport, string>({
    selectId: (report) => report.party_id,
});

const initialState: ROReportsState = {
    reasoned_orders: roAdapter.getInitialState(),
    status: "idle",
    error: null,
    report_status: null,
};

const addFetchCases = (
    builder: ActionReducerMapBuilder<ROReportsState>,
    thunk: any
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "loading";
            state.error = null;
            roAdapter.removeAll(state.reasoned_orders);
        })

        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = "Failed to fetch RO Reports";
        })

        .addCase(thunk.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.error = null;
            roAdapter.setAll(state.reasoned_orders,action.payload.ro_reports);
        });
};

const addCreateCases = (
    builder: ActionReducerMapBuilder<ROReportsState>,
    thunk: any
) => {
    builder
        .addCase(thunk.pending, (state, { meta }) => {
            const { party_id } = meta.arg.request;

            roAdapter.upsertOne(state.reasoned_orders, {
                report_id: party_id,
                party_id,
                status: ReportStatus.SUBMITTED,
                last_updated_at: new Date().toISOString(),
            });
        })

        .addCase(thunk.rejected, (state, { meta }) => {
            const { party_id } = meta.arg.request;

            roAdapter.updateOne(state.reasoned_orders, {
                id: party_id,
                changes: {
                    status: ReportStatus.FAILED,
                },
            });
        })

        .addCase(thunk.fulfilled, (state, action) => {
            const { party_id } = action.meta.arg.request;

            roAdapter.updateOne(state.reasoned_orders, {
                id: party_id,
                changes: {
                    status: ReportStatus.IN_PROGRESS,
                    last_updated_at: new Date().toISOString(),
                },
            });
        });
};

const addUpdateCases = (
    builder: ActionReducerMapBuilder<ROReportsState>,
    thunk: any
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "saving";
            state.error = null;
        })

        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error ="Failed to update RO Report";
        })

        .addCase(thunk.fulfilled, (state, action) => {
            const id = action.meta.arg.request.party_id;
            const { content } = action.meta.arg.request;
            const report_id = action.meta.arg.report_id;
            const existingEntity = state.reasoned_orders.entities[id];

            roAdapter.upsertOne(state.reasoned_orders, {
                ...existingEntity, 
                party_id: id,
                report_id: existingEntity?.report_id ?? report_id,
                content,
                status: ReportStatus.AVAILABLE,
                last_updated_at: new Date().toISOString(),
            });

            state.status = "succeeded";
            state.error = null;
        });
};

const addFetchSinglePartyCases = (
    builder: ActionReducerMapBuilder<ROReportsState>,
    thunk: any
) => {
    builder
        .addCase(thunk.pending, (state, { meta }) => {
            const { party_id } = meta.arg;

            roAdapter.updateOne(state.reasoned_orders, {
                id: party_id,
                changes: {
                    status: ReportStatus.IN_PROGRESS,
                },
            });
        })

        .addCase(thunk.rejected, (state, { meta }) => {
            const { party_id } = meta.arg;

            roAdapter.updateOne(state.reasoned_orders, {
                id: party_id,
                changes: {
                    status: ReportStatus.FAILED,
                },
            });
        })

        .addCase(thunk.fulfilled, (state, action) => {
            const report = action.payload.ro_reports[0];
            state.report_status = ReportStatus.AVAILABLE;

            roAdapter.upsertOne(
                state.reasoned_orders,
                report
            );
        });
};

const roSlice = createSlice({
    name: "reasonedOrder",
    initialState,
    reducers: {},

    extraReducers: (builder) => {
        addFetchCases(builder,fetchLegacyReasonedOrdersThunk);

        addCreateCases(builder,createLegacyReasonedOrderThunk);

        addUpdateCases(builder,upsertLegacyReasonedOrderThunk);

        addFetchSinglePartyCases(builder,fetchSinglePartyLegacyROReportThunk);

        addFetchCases(builder,fetchReasonedOrdersThunk);

        addCreateCases(builder,createReasonedOrderThunk);

        addUpdateCases(builder,upsertReasonedOrderThunk);

        addFetchSinglePartyCases(builder,fetchSinglePartyROReportThunk);
    },
});

export const roReducer = roSlice.reducer;

const roSelectors = roAdapter.getSelectors<RootState>(
    (state) => state.ro.reasoned_orders
);

export const {
    selectEntities: selectROReportsData,
} = roSelectors;

export const selectROReports = (state: RootState): ROReportsState => state.ro;

export const selectROStatus = (state: RootState) =>state.ro.status;

export const selectROReportStatus = (state: RootState): ReportStatus | null =>state.ro.report_status;

export const selectROReportError = (state: RootState): string | null =>state.ro.error;